import { useDispatch, useSelector } from "react-redux";
import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";
import DeleteIcon from "@mui/icons-material/Delete";
import { red } from "@mui/material/colors";
import { Grid } from "@mui/material";
import {
  Button,
  ButtonGroup,
  Stack,
  useFinanceableTheme,
  Tooltip,
  CircularProgress,
} from "@financeable-com-au/financeable-ui";
import {
  setRemoveAssetModalOptions,
  getCloneAsset,
  makeAssetPrimary,
  userDetailsSelector,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { ERRORS_MESSAGE } from "@/constants";
import { AssetActionsProps, IDispatchResponse } from "@/utils/interface";
import { UserDetailsState } from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import palette from "@/theme/palette";

const AssetActionsLoader = () => {
  return <CircularProgress size="sm" style={{ margin: "4px" }} />;
};

const AssetActions = ({ index, asset, applicationId, isDisableEdit }: AssetActionsProps) => {
  const theme = useFinanceableTheme();
  const dispatch = useDispatch();
  const userDetails = useSelector(userDetailsSelector) as UserDetailsState;
  const assetId = asset._id;
  const { cloneAssetLoaderOptions, makeAssetPrimaryLoaderOptions, removeAssetModalOptions } = userDetails;

  const cloneAssetLoader = cloneAssetLoaderOptions?.isLoading && cloneAssetLoaderOptions?.assetId === assetId;
  const makeAssetPrimaryLoader =
    makeAssetPrimaryLoaderOptions?.isLoading && makeAssetPrimaryLoaderOptions?.assetId === assetId;
  const removeAssetLoader = removeAssetModalOptions?.isRemovingAsset && removeAssetModalOptions?.assetId === assetId;

  const handle = {
    cloneAsset: () => {
      dispatch(getCloneAsset({ assetId, applicationId })).then((res: IDispatchResponse) => {
        if (res?.error) {
          dispatch(
            openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || "Asset cloned successfully",
              intent: "success",
            }),
          );
        }
      });
    },
    makeAssetPrimary: () => {
      const makeAssetPrimaryPayload = {
        applicationId,
        assetId: assetId,
        order: index,
      };

      dispatch(makeAssetPrimary(makeAssetPrimaryPayload)).then((res: IDispatchResponse) => {
        if (res?.error) {
          dispatch(
            openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || "Asset made primary successfully",
              intent: "success",
            }),
          );
        }
      });
    },
    removeAsset: async () => {
      dispatch(
        setRemoveAssetModalOptions({
          isOpen: true,
          assetType: asset.assetType,
          assetId,
        }),
      );
    },
  };

  const defaultButtonStyle = {
    "&:hover": { backgroundColor: theme.palette.primary[100] },
    borderColor: theme.palette.divider,
  } as const;

  const defaultTooltipStyle = {
    placement: "bottom",
    size: "sm",
  } as const;

  const makePrimaryButtonTitle = index === 0 ? "Primary asset" : "Set as Primary";

  return (
    <Stack direction="row" justifyContent="flex-end">
      <ButtonGroup
        variant="outlined"
        color="primary"
        sx={{
          width: "100%",
          "& .MuiButton-root": {
            border: "none",
            borderRadius: 0,
          },
          "& .MuiButtonGroup-grouped:not(:last-of-type)": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Grid
          container
          alignItems="center"
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            "& > .MuiGrid-item": {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              textTransform: "none",
            },
            "& > .MuiGrid-item:hover": {
              backgroundColor: palette.disabledBackground,
              cursor: "pointer",
            },
          }}
        >
          <Grid item xs={8}>
            <Button
              variant="secondary"
              intent="neutral"
              size="sm"
              disabled={isDisableEdit || makeAssetPrimaryLoader || index === 0}
              onClick={handle.makeAssetPrimary}
              style={{
                ...defaultButtonStyle,
                fontSize: "12px",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                marginRight: "15px",
                padding: "4px 8px",
                minWidth: "95px",
              }}
            >
              {makeAssetPrimaryLoader ? <AssetActionsLoader /> : makePrimaryButtonTitle}
            </Button>
          </Grid>
          <Grid item lg={2}>
            <Tooltip title="Clone" {...defaultTooltipStyle}>
              <Button
                variant="secondary"
                intent="neutral"
                disabled={isDisableEdit ||cloneAssetLoader}
                onClick={handle.cloneAsset}
                style={{ ...defaultButtonStyle, color: theme.palette.primary[200], backgroundColor: "transparent" }}
              >
                {cloneAssetLoader ? <AssetActionsLoader /> : <ContentCopyTwoToneIcon style={{ fontSize: "20px" }} />}
              </Button>
            </Tooltip>
          </Grid>
          <Grid item lg={2}>
            <Tooltip title="Delete" {...defaultTooltipStyle}>
              <Button
                variant="secondary"
                intent="neutral"
                disabled={isDisableEdit ||removeAssetLoader || index === 0}
                onClick={handle.removeAsset}
                style={{ ...defaultButtonStyle, ...(index !== 0 && { color: red[500] }), backgroundColor: "transparent" }}
              >
                <DeleteIcon style={{ fontSize: "20px" }} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </ButtonGroup>
    </Stack>
  );
};

export default AssetActions;
